// The dojo (`?scene=dojo`, gates B1 and B2): the valley's machinery (the Wanderer on its controller, the camera, the
// look) on flat ground with three training dummies from the `dojo` table: one that stands, one that swings on a timer
// behind a telegraph, one with an unblockable slam. Combat runs in the one kernel game as the "combat" module:
//   combat:act      (order -10)  the combat's step begins (timers, phases), then the hero's presses through the input
//                                buffer at their first legal moment, the held block, the weapon switch, the dummies'
//                                scripts, queued test requests and respawns;
//   valley:controllers (order 0) the hero's controller steps with combat's drive (a lunge, a roll; rooted while acting);
//   valley:physics step (order 100)
//   combat:resolve  (order 150)  hit volumes sweep the hurt sensors, contacts resolve, events go out.
// The combat state and the dojo's own state are kernel components (saved and hashed). The hit-stop and slow-mo go to
// the runtime clock, the crits roll on the host's seeded "combat" stream.
import type { ColliderSpec, LayerTable } from "@rpg/collision";
import { chooseTarget, createCombat, emptyCombatState, type TargetWeights, type Combat, type CombatEvents, type CombatState, type FighterState, type Intent, type Vec3 } from "@rpg/combat";
import { stickToWorld, type MoveDrive } from "@rpg/controller";
import type { GameModule, World } from "@rpg/kernel";
import type { Runtime } from "@rpg/runtime";
import type { GameEvents } from "../core";
import type { Course } from "../valley/course";
import { VALLEY_LAYERS } from "../valley/course";
import type { ValleyRig, ValleyVariant } from "../valley/scene";
import type { CombatContent } from "./content";
import type { SpikeContent } from "../content";
import { targetingValues } from "../targets/targeting";
import { BODIES_C, bodiesTables, createBodies, type Bodies } from "../bodies/module";

export const COMBAT_C = "combat/State";
export const DOJO_C = "dojo/State";

/** The dojo's layers: the valley's, plus one hurt layer per team (sensors only; they collide with nothing). */
export const DOJO_LAYERS: LayerTable = { ...VALLEY_LAYERS, hurtHero: [], hurtFoe: [], ragdoll: ["terrain"] };
/** The floor's half size, m. */
const FLOOR = 14;
/** Where the hero starts: facing the dummies (yaw π faces -Z). */
export const DOJO_SPAWN = { x: 0, z: 4, yaw: Math.PI };
/** The stub mount stands out of the way. */
const MOUNT_SPAWN = { x: -11, z: 11, yaw: Math.PI };

/** A dummy's capsule (its solid body) and how far its centre sits above its feet. */
const DUMMY_BODY = { radius: 0.45, halfHeight: 0.5 };

/** The dojo's own state (a kernel component). */
export interface DojoState {
  /** The hero's fighter (its kernel entity). */
  hero: number;
  dummies: { entity: number; place: string; fighter: string; body: string; feet: [number, number, number]; yaw: number; every: number; offset: number }[];
  /** The dummies swing on their timers. */
  scripted: boolean;
  /** Steps since the dojo began. */
  step: number;
  /** Fighters waiting to stand again: entity → steps left. */
  respawn: Record<string, number>;
  /** Test requests waiting for their step: who, what, the step to make it on; and what each got. */
  queue: { who: number; intent: Intent; at: number }[];
  results: { who: number; kind: string; at: number; taken: boolean }[];
  /** The block came from the held button (released with it), not from a test request. */
  heldBlock: boolean;
  /** Knockbacks in progress (the bodies module's reactions on the hero): a ground velocity for so many steps. */
  knocks: { entity: number; vel: [number, number]; left: number }[];
}

/** One combat event as the dojo logs it (the sim step it happened on). */
export interface LoggedEvent {
  step: number;
  name: keyof CombatEvents;
  payload: CombatEvents[keyof CombatEvents];
}

/** What the dojo exposes once its world is built. */
export interface Dojo {
  combat: Combat;
  state(): DojoState;
  /** The last 2000 combat events, oldest first. */
  log: LoggedEvent[];
  /** Every swept step of a live hit volume (the last 4000). */
  sweeps: { entity: number; move: string; step: number; at: Vec3; radius: number }[];
  /** A fighter's feet and facing now. */
  pose(entity: number): { feet: Vec3; yaw: number };
  /** The entity of a dummy by its place id ("static", "attacker", "slammer"). */
  dummy(place: string): number;
  /** A fighter's whole combat state (the view poses from it). */
  fighter(entity: number): FighterState | undefined;
  /** Listen to every combat event as it is emitted (in the sim step; views only read). */
  onEvent(fn: (name: keyof CombatEvents, payload: CombatEvents[keyof CombatEvents]) => void): void;
  rig: ValleyRig;
  /** The bodies module (poses, reactions, ragdolls). */
  bodies: Bodies;
}

/** The dojo's flat course: a floor on the terrain layer (the probes, the camera and the below-ground check see it) and
 *  four posts at the corners. */
function dojoCourse(): Course {
  const posts = [
    [-FLOOR + 1.5, -FLOOR + 1.5],
    [FLOOR - 1.5, -FLOOR + 1.5],
    [-FLOOR + 1.5, FLOOR - 1.5],
    [FLOOR - 1.5, FLOOR - 1.5],
  ] as const;
  return {
    bodies: [
      { id: "terrain", kind: "fixed", position: { x: 0, y: -0.5, z: 0 }, colliders: [{ shape: { type: "cuboid", halfExtents: { x: FLOOR, y: 0.5, z: FLOOR } }, layer: "terrain", tag: "grass" }] },
      ...posts.map(([x, z], i) => ({ id: `post-${i}`, kind: "fixed" as const, position: { x, y: 1, z }, colliders: [{ shape: { type: "cylinder" as const, halfHeight: 1, radius: 0.25 }, layer: "solid" }] })),
    ],
    water: [],
    steps: [],
    ramps: [],
    stepLaneZ: 0,
  };
}

/**
 * The dojo as a variant of the valley. `ready` is called with the dojo once the valley's rig exists (before the world
 * is built); the view and the handle hold on to it.
 */
export function dojoVariant(o: { content: CombatContent; spike: SpikeContent; runtime: Runtime; ready(d: Dojo): void }): ValleyVariant {
  const c = o.content;
  const heroRow = c.fighters.get("hero");
  if (!heroRow) throw new Error("dojo: no hero fighter row");
  const heroHurt: ColliderSpec = { shape: { type: "capsule", halfHeight: heroRow.hurt.halfHeight, radius: heroRow.hurt.radius }, layer: "hurtHero", sensor: true };
  return {
    name: "dojo",
    layers: DOJO_LAYERS,
    course: dojoCourse,
    height: () => 0,
    spawn: DOJO_SPAWN,
    mountSpawn: MOUNT_SPAWN,
    heroColliders: [heroHurt],
    extend({ rig, rng, actions }) {
      const hz = o.runtime.clock.stepHz;
      const combatState: CombatState = emptyCombatState();
      let world: World | null = null;
      const dojoState = (): DojoState => {
        const e = world!.query(DOJO_C)[0];
        return world!.getComponent<DojoState>(e!, DOJO_C)!;
      };
      let emit: (<K extends keyof CombatEvents>(name: K, p: CombatEvents[K]) => void) | null = null;
      const log: LoggedEvent[] = [];
      const listeners: ((name: keyof CombatEvents, payload: CombatEvents[keyof CombatEvents]) => void)[] = [];
      const sweeps: Dojo["sweeps"] = [];
      const random = rng.stream("combat");
      const softWeights = targetingValues(o.spike).soft as TargetWeights;
      const pose = (entity: number): { feet: Vec3; yaw: number } => {
        const d = dojoState();
        if (entity === d.hero) {
          const h = rig.hero();
          return { feet: [h.feet.x, h.feet.y, h.feet.z], yaw: h.yaw };
        }
        const m = d.dummies.find((x) => x.entity === entity);
        if (!m) throw new Error(`dojo: no fighter ${entity}`);
        return { feet: m.feet, yaw: m.yaw };
      };
      const combat = createCombat({
        stepHz: hz,
        rules: c.rules,
        moves: c.moves,
        weapons: c.weapons,
        stats: c.stats,
        world: rig.collision,
        pose,
        state: combatState,
        emit: (name, payload) => {
          log.push({ step: dojoState().step, name, payload });
          if (log.length > 2000) log.shift();
          emit?.(name, payload);
          for (const l of listeners) l(name, payload);
        },
        // hit-stop has one owner since the merge: the impact director (src/feedback/), by the hit's kind; combat keeps
        // its slow-mo
        clock: { slowMo: (scale, s, ease) => o.runtime.clock.slowMo(scale, s, ease) },
        random: () => random.next(),
        // the soft target when a move starts (the targeting chooser, gate B3, with its soft weights)
        chooseTarget: (from, candidates) => chooseTarget(from, candidates, softWeights),
        onSweep: (entity, move, _moveStep, at, radius) => {
          sweeps.push({ entity, move, step: dojoState().step, at, radius });
          if (sweeps.length > 4000) sweeps.shift();
        },
      });
      if (c.issues.length) console.error(`[combat content] ${c.issues.join("; ")}`);

      // the hero's input: buffered presses at their first legal moment, the held block, the weapon switch
      const BUFFERED: Record<string, Intent["kind"]> = { attack: "attack", skill: "skill", bigMove: "big", dodge: "dodge" };
      const heroInput = (d: DojoState): void => {
        const hero = d.hero;
        const buf = actions.buffer;
        const pending = buf?.action ?? null;
        if (pending && BUFFERED[pending]) {
          const kind = BUFFERED[pending]!;
          const stick = stickToWorld(actions.stick("move"), rig.control().cameraYaw);
          const dir: Vec3 | undefined = kind === "dodge" && Math.hypot(stick.x, stick.z) > 0.2 ? [stick.x, 0, stick.z] : undefined;
          if (combat.request(hero, dir ? { kind, dir } : { kind })) buf!.consume(pending);
        } else if (!buf) {
          for (const [action, kind] of Object.entries(BUFFERED)) if (actions.pressed(action) && combat.request(hero, { kind })) break;
        }
        const held = actions.down("block");
        const v = combat.state(hero);
        if (held && v.defending !== "block") {
          if (combat.request(hero, { kind: "block" })) d.heldBlock = true;
        } else if (!held && d.heldBlock && v.defending === "block") {
          combat.request(hero, { kind: "blockRelease" });
          d.heldBlock = false;
        }
        if (actions.pressed("weaponSwitch")) {
          const owned = c.weapons.filter((w) => c.attachmentOf.get(w.id)).map((w) => w.id);
          const i = owned.indexOf(v.weapon ?? "");
          combat.setWeapon(hero, owned[(i + 1) % owned.length] ?? null);
        }
      };

      const module: GameModule<GameEvents> = {
        id: "combat",
        version: "0.1.0",
        dependencies: ["valley"],
        setup(ctx) {
          emit = (name, payload) => ctx.events.emit(name, payload as never);
          ctx.events.on("combat:defeated", ({ entity }) => {
            dojoState().respawn[String(entity)] = Math.round(c.respawn * hz);
          });
          ctx.registerSystem(
            () => {
              const d = dojoState();
              d.step++;
              combat.begin();
              for (const [k, left] of Object.entries(d.respawn)) {
                if (left > 1) d.respawn[k] = left - 1;
                else {
                  delete d.respawn[k];
                  combat.revive(Number(k));
                }
              }
              heroInput(d);
              for (const k of d.knocks) k.left--;
              d.knocks = d.knocks.filter((k) => k.left > 0);
              if (d.scripted)
                for (const m of d.dummies) if (m.every > 0 && d.step >= m.offset && (d.step - m.offset) % m.every === 0) combat.request(m.entity, { kind: "attack" });
              const due = d.queue.filter((q) => q.at <= d.step);
              d.queue = d.queue.filter((q) => q.at > d.step);
              for (const q of due) {
                const taken = combat.request(q.who, q.intent);
                d.results.push({ who: q.who, kind: q.intent.kind, at: d.step, taken });
                if (d.results.length > 200) d.results.shift();
              }
            },
            { name: "combat:act", order: -10 },
          );
          ctx.registerSystem(() => combat.resolve(), { name: "combat:resolve", order: 150 });
          ctx.registerSaveSlice({
            serialize: () => ({ combat: structuredClone(combatState), dojo: structuredClone(dojoState()) }),
            deserialize: (data) => {
              const s = data as { combat: CombatState; dojo: DojoState };
              combatState.fighters = structuredClone(s.combat.fighters);
              Object.assign(dojoState(), structuredClone(s.dojo));
            },
          });
        },
      };

      const build = (w: World): void => {
        world = w;
        const heroEntity = w.query("valley/Hero")[0]!;
        const dummies: DojoState["dummies"] = [];
        for (const place of c.dojo) {
          const row = c.fighters.get(place.fighter.id);
          if (!row) throw new Error(`dojo: no fighter "${place.fighter.id}"`);
          const entity = w.createEntity();
          const body = `dummy-${place.id}`;
          const [x, z] = place.at as [number, number];
          rig.collision.add({
            id: body,
            kind: "kinematic",
            position: { x, y: DUMMY_BODY.halfHeight + DUMMY_BODY.radius, z },
            colliders: [
              { shape: { type: "capsule", halfHeight: DUMMY_BODY.halfHeight, radius: DUMMY_BODY.radius }, layer: "solid" },
              { shape: { type: "capsule", halfHeight: row.hurt.halfHeight, radius: row.hurt.radius }, layer: "hurtFoe", sensor: true },
            ],
          });
          combat.addFighter(entity, { team: row.team, body, hurtLayer: "hurtFoe", weapon: row.weapon?.id ?? null, stats: row.stats as Record<string, number> });
          dummies.push({
            entity,
            place: place.id,
            fighter: row.id,
            body,
            feet: [x, 0, z],
            yaw: place.yaw,
            every: place.script.kind === "repeat" ? Math.round((place.script.every ?? 2) * hz) : 0,
            offset: place.script.kind === "repeat" ? Math.round((place.script.offset ?? 0) * hz) : 0,
          });
        }
        combat.addFighter(heroEntity, { team: heroRow.team, body: "hero", hurtLayer: "hurtHero", weapon: heroRow.weapon?.id ?? null, stats: heroRow.stats as Record<string, number> });
        w.addComponent<CombatState>(w.createEntity(), COMBAT_C, combatState);
        w.addComponent<DojoState>(w.createEntity(), DOJO_C, { hero: heroEntity, dummies, scripted: true, step: 0, respawn: {}, queue: [], results: [], heldBlock: false, knocks: [] });
        rig.collision.step();
      };

      const heroDrive = (): MoveDrive | null => {
        if (!world) return null;
        const hero = dojoState().hero;
        const f = combatState.fighters[hero];
        if (f && !f.alive) return { x: 0, z: 0 };
        const kb = dojoState().knocks.find((k) => k.entity === hero);
        if (kb) return { x: kb.vel[0], z: kb.vel[1] };
        return combat.drive(hero);
      };

      // the bodies (phase B5): the fighters' action poses and hit reactions from the combat events, the dummies' ragdolls
      // (the hero stands again instead); a knockback moves the hero, the dummies are planted
      const bodies = createBodies({
        collision: rig.collision,
        tables: bodiesTables(o.spike),
        layer: "ragdoll",
        lookup: {
          card: (e) => {
            const d = dojoState();
            if (e === d.hero) return combatState.fighters[e]?.alive === false ? null : (c.fighters.get("hero")?.card.id ?? null);
            const m = d.dummies.find((x) => x.entity === e);
            return m ? (c.fighters.get(m.fighter)?.card.id ?? null) : null;
          },
          root: (e) => {
            const p = pose(e);
            const h = e === dojoState().hero ? rig.hero() : null;
            return { position: p.feet, yaw: p.yaw, ...(h ? { velocity: [h.vel.x, h.vel.y, h.vel.z] as const } : {}) };
          },
          knockback: (e, kb) => {
            const d = dojoState();
            if (e !== d.hero) return;
            const steps = Math.max(1, Math.round(kb.seconds * hz));
            const speed = kb.distance / kb.seconds;
            d.knocks = d.knocks.filter((k) => k.entity !== e);
            d.knocks.push({ entity: e, vel: [kb.dir[0] * speed, kb.dir[2] * speed], left: steps });
          },
        },
      });
      o.ready({
        combat,
        state: dojoState,
        log,
        sweeps,
        pose,
        dummy: (place) => dojoState().dummies.find((m) => m.place === place)?.entity ?? -1,
        fighter: (e) => combatState.fighters[e],
        onEvent: (fn) => void listeners.push(fn),
        rig,
        bodies,
      });
      return { modules: [module, bodies.module], build, hashed: [COMBAT_C, DOJO_C, BODIES_C], heroDrive };
    },
  };
}
